import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import moment from "moment";
import MDButton from "components/MDButton";

const STEPS = [
  { key: "first", label: "First email" },
  { key: "reminder_1", label: "Reminder 1" },
  { key: "reminder_2", label: "Reminder 2" },
  { key: "reminder_3", label: "Reminder 3" },
];

const ANSWER_LABEL = {
  not_sent: "No email yet",
  waiting: "Sent — waiting",
  opened: "Opened",
  replied: "Replied",
};

const ink = {
  title: "#0f172a",
  body: "#1e293b",
  muted: "#475569",
  label: "#64748b",
  border: "#cbd5e1",
  chipBg: "#f1f5f9",
  chipText: "#1e293b",
};

const outlinedChipSx = {
  bgcolor: `${ink.chipBg} !important`,
  color: `${ink.chipText} !important`,
  borderColor: `${ink.border} !important`,
  fontWeight: 600,
  "& .MuiChip-label": { color: `${ink.chipText} !important` },
  "& .MuiChip-icon": { color: `${ink.muted} !important` },
};

const filledChipSx = {
  fontWeight: 600,
  "& .MuiChip-label": { color: "#ffffff !important" },
  "& .MuiChip-icon": { color: "#ffffff !important" },
};

function Field({ label, value }) {
  const text = value == null || value === "" ? "—" : String(value);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ display: "block", color: ink.label }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word", color: ink.body }}>
        {text}
      </Typography>
    </Box>
  );
}

function answerChipProps(answer) {
  if (answer === "replied") return { color: "success", sx: filledChipSx };
  if (answer === "opened") return { color: "info", sx: filledChipSx };
  if (answer === "waiting") return { color: "warning", sx: filledChipSx };
  return { variant: "outlined", sx: outlinedChipSx };
}

export default function CustomerCard({
  row,
  statusLabel,
  statusColor,
  flag,
  universityName,
  name,
  onOpen,
  onMenu,
  onEmail,
}) {
  const summary = row.email_summary || {};
  const steps = summary.steps || {};
  const firstEmailSent =
    Boolean(summary.first_sent) || (steps.first?.sent_count || 0) > 0;
  const answer = summary.answer_status || "not_sent";
  const dates = Array.isArray(row.demo_dates) ? row.demo_dates.filter(Boolean).join(", ") : "";
  const statusKey = row.status || "new_request";
  const statusTone = statusColor(statusKey);
  const statusChip =
    statusTone === "default"
      ? { variant: "outlined", sx: outlinedChipSx }
      : { color: statusTone, sx: filledChipSx };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: "1px solid",
        borderColor: answer === "replied" ? "success.light" : ink.border,
        height: "100%",
        bgcolor: "#ffffff",
      }}
    >
      <Stack spacing={1.5}>
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3, color: ink.title }}>
              {flag ? `${flag} ` : ""}
              {name || "—"}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-all", color: ink.muted }}>
              {row.work_email || "—"}
            </Typography>
          </Box>
          <IconButton size="small" aria-label="Customer actions" onClick={onMenu} sx={{ color: ink.muted }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={statusLabel(statusKey)} {...statusChip} />
          <Chip
            size="small"
            variant="outlined"
            label={row.source === "manual" ? "Added manually" : "Demo request"}
            sx={outlinedChipSx}
          />
          <Chip
            size="small"
            icon={
              answer === "replied" ? (
                <ReplyOutlinedIcon />
              ) : answer === "opened" ? (
                <DraftsOutlinedIcon />
              ) : (
                <MarkEmailReadOutlinedIcon />
              )
            }
            label={ANSWER_LABEL[answer] || answer}
            {...answerChipProps(answer)}
          />
          <Chip size="small" variant="outlined" label={`Sent ${summary.sent_count || 0}`} sx={outlinedChipSx} />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 0.75,
          }}
        >
          {STEPS.map((step) => {
            const info = steps[step.key] || {};
            const sent = info.sent_count || 0;
            const opened = Boolean(info.opened);
            const replied = Boolean(info.replied);
            const unlocked =
              step.key === "first" ||
              firstEmailSent ||
              info.unlocked === true ||
              sent > 0;
            // Sent → yellow; opened (or replied) → green; locked/not sent → muted.
            let tone = {
              bgcolor: "#f8fafc",
              borderColor: ink.border,
              title: ink.title,
              meta: ink.muted,
            };
            if (opened || replied) {
              tone = {
                bgcolor: "#16a34a",
                borderColor: "#15803d",
                title: "#ffffff",
                meta: "rgba(255,255,255,0.9)",
              };
            } else if (sent > 0) {
              tone = {
                bgcolor: "#eab308",
                borderColor: "#ca8a04",
                title: "#ffffff",
                meta: "rgba(255,255,255,0.92)",
              };
            } else if (!unlocked) {
              tone = {
                bgcolor: "#f1f5f9",
                borderColor: "#e2e8f0",
                title: "#94a3b8",
                meta: "#94a3b8",
              };
            }
            let statusText = "Not sent";
            if (!unlocked && !sent) statusText = "Locked";
            else if (replied) statusText = sent > 1 ? `${sent} sent · replied` : "Replied";
            else if (opened) statusText = sent > 1 ? `${sent} sent · opened` : "Opened";
            else if (sent) statusText = `${sent} sent`;

            return (
              <Box
                key={step.key}
                sx={{
                  p: 0.75,
                  borderRadius: 1.5,
                  bgcolor: tone.bgcolor,
                  border: "1px solid",
                  borderColor: tone.borderColor,
                  opacity: !unlocked && !sent ? 0.85 : 1,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ display: "block", lineHeight: 1.2, color: `${tone.title} !important` }}
                >
                  {step.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: `${tone.meta} !important`, fontWeight: 600 }}
                >
                  {statusText}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.25,
          }}
        >
          <Field label="First name" value={row.first_name} />
          <Field label="Last name" value={row.last_name} />
          <Field label="Registered email" value={row.user?.email} />
          <Field label="Phone" value={row.phone || row.user?.phone} />
          <Field label="University" value={universityName} />
          <Field label="Country" value={row.country || row.user?.country} />
          <Field label="Academic role" value={row.academic_role} />
          <Field label="Account role" value={row.account_role} />
          <Field label="Teaching area" value={row.teaching_area} />
          <Field label="Class size" value={row.class_size} />
          <Field label="Main interest" value={row.main_interest} />
          <Field label="Main goal" value={row.main_goal} />
          <Field label="Preferred dates" value={dates} />
          <Field label="Time preference" value={row.time_preference} />
          <Field label="Pilot" value={row.pilot} />
          <Field label="Submitted" value={row.created_at ? moment(row.created_at).format("YYYY-MM-DD HH:mm") : ""} />
          <Field label="Notes" value={row.notes} />
          <Field label="Linked account" value={row.user_id} />
        </Box>

        <Stack direction="row" spacing={1}>
          <MDButton
            size="small"
            color="info"
            variant="gradient"
            onClick={onEmail}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Send email
          </MDButton>
          <ButtonLink onClick={onOpen}>Full record</ButtonLink>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ButtonLink({ onClick, children }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        border: 0,
        background: "transparent",
        cursor: "pointer",
        px: 1.25,
        py: 0.75,
        borderRadius: 1.5,
        fontWeight: 700,
        fontSize: "0.8125rem",
        color: "#1e293b",
        "&:hover": { bgcolor: "#f1f5f9", color: "#0f172a" },
      }}
    >
      {children}
    </Box>
  );
}
